import { useCallback, useMemo, useState } from "react";
import { Box, BoxBackGround } from "../../common/Box";
import {
  ButtonPriority,
  ButtonType,
  ButtonWrapper,
  TextSize,
} from "../../common/Button";
import { TextField } from "../../common/Form/TextBoxField";
import {
  UnOrderedList,
  UnOrderedListItem,
} from "../../common/List/UnOrderedList/UnOrderedList";
import { PageTitle } from "../../common/PageTitle";
import { Text } from "../../common/Text";
import TopTemplate from "../../common/templates/top/TopTemplate";
import axios from "axios";
import base64url from "base64url";
import { config } from "../../common/config/Config";
import { useHistory } from "react-router";
import { HashLink as Link } from "react-router-hash-link";
import { validateTermName } from "../../common/TerminalNameValidator/termValidator";
import BlankIcon from "../../assets/img/icn-_blank.svg";
import {
  BIOMETRIC_INFO_PAGE_URL_WITH_ANCHOR_03,
  BROWSER_NOT_SUPPORT_ERROR,
  EMPTY_REGISTER_TERMINAL_NAME_ERROR,
  FEATURE_NOT_AVAILABLE_ERROR,
  MAX_DEVICE_ERROR,
  REGISTER_MAX_DEVICE_ERROR_CODE,
  SYSTEM_ERROR,
  TERMINAL_NAME_VALIDATOR_FAILED_ERROR,
  WEBAUTHN_API_ERROR,
  WEBAUTHN_API_ERROR_TEXT_LINK,
  WEBAUTHN_API_ERROR_2,
  WEBAUTHN_API_ERROR_2_TEXT_LINK,
  WEBAUTHN_API_ERROR_3
} from "../../common/Constant/Constant";

const BiometricRegistrationContainer = () => {
  const history = useHistory();
  const [error, setError] = useState<any>();
  const [browserNotSupportError, setBrowserNotSupportError] = useState<boolean>(false);
  const [webAuthnError, setWebAuthnError] = useState<boolean>(false);
  const [isMaxRegisteredDevice, setIsMaxRegisteredDevice] = useState<boolean>(false);
  const [status, setStatus] = useState<any>();
  const [userSupportedState, setUserSupportedState] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const registerDevice = useCallback(async (device: string) => {
    setError("");
    setBrowserNotSupportError(false);
    setWebAuthnError(false);
    setIsMaxRegisteredDevice(false);
    if (!window.PublicKeyCredential) {
      setBrowserNotSupportError(true);
      return;
    }
    const deviceName = device.trim();
    if (!deviceName) {
      setError(EMPTY_REGISTER_TERMINAL_NAME_ERROR);
      return;
    }
    if (!validateTermName(deviceName)) {
      setError(TERMINAL_NAME_VALIDATOR_FAILED_ERROR);
      return;
    }
    try {
      setIsProcessing(true);
      const response = await axios.post(
        `${config.apiBaseUrl}${config.apiCreate}`,
        {}
      );
      if (response.data.errorCode && response.data.errorCode === REGISTER_MAX_DEVICE_ERROR_CODE) {
        setIsMaxRegisteredDevice(true);
        return;
      }
      let credential: any;
      try {
        credential = await navigator.credentials.create({
          publicKey: {
            rp: {
              name: response.data.rp.name,
              id: response.data.rp.id,
            },
            user: {
              name: response.data.user.name,
              displayName: response.data.user.displayName,
              id: base64url.toBuffer(response.data.user.id),
            },
            challenge: base64url.toBuffer(response.data.challenge),
            pubKeyCredParams: response.data.pubKeyCredParams,
            timeout: response.data.timeout,
            excludeCredentials: response.data.excludeCredentials.map(
              (item: any) => ({
                id: base64url.toBuffer(item.id),
                type: item.type,
              })
            ),
            authenticatorSelection: response.data.authenticatorSelection,
            attestation: response.data.attestation,
            extensions: response.data.extensions,
          },
        });
      } catch (error) {
        setWebAuthnError(true);
        return;
      }

      const publicCredential = {
        id: credential.id,
        rawId: base64url(credential.rawId),
        response: {
          attestationObject: base64url(credential.response.attestationObject),
          clientDataJSON: base64url(credential.response.clientDataJSON),
        },
        type: credential.type,
      };
      const result = await axios.post(
        `${config.apiBaseUrl}${config.apiResult}`,
        {
          authenticator_name: deviceName,
          credential: publicCredential,
        }
      );
      setStatus(result.data.status);
    } catch (err:any) {
      if (err.response.status === 403) {
        setUserSupportedState(false);
        return;
      }
      setError(SYSTEM_ERROR);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  if (!userSupportedState) {
    return (
      <TopTemplate>
        <BioRegistrationFailPresentation
          error={FEATURE_NOT_AVAILABLE_ERROR}
          onBackButtonClick={() => {
            history.push("/authenticator-manager");
          }}
        />
      </TopTemplate>
    );
  }

  if (status === "ok") {
    return (
      <TopTemplate>
        <BioRegistrationSuccessPresentation
          onConfirmButtonClick={() => {
            history.push("/authenticator-manager#anc-01");
          }}
        />
      </TopTemplate>
    );
  }

  return (
    <TopTemplate>
      <>
        <PageTitle 
          subText="生体認証ログイン設定"
          text="端末の登録" 
          title="生体認証の登録"
        />
        {error ? <ErrorMessageForm errorMessages={error} /> : null}
        {browserNotSupportError ? <BrowserNotSupportErrorMessageForm /> : null}
        {webAuthnError ? <WebAuthApiErrorMessageForm /> : null}
        {isMaxRegisteredDevice ? <MaxDeviceErrorMessageForm /> : null}
        <BiometricRegistrationPresentation
          onLoginClick={registerDevice}
          isProcessing={isProcessing}
        />
      </>
    </TopTemplate>
  );
};

interface BiometricRegistrationPresentationProps {
  onLoginClick: (device: string) => void;
  isProcessing: boolean;
}

const BiometricRegistrationPresentation = (
  props: BiometricRegistrationPresentationProps
) => {
  const history = useHistory();
  const { onLoginClick } = props;
  const getDefaultDevice = () => {
    var userAgent = window.navigator.userAgent,
      platform = window.navigator.platform,
      iPadSafariPlatforms = ["MacIntel"],
      macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
      windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
      iosPlatforms = ["iPhone", "iPad", "iPod"],
      os = "Other";
    if (iPadSafariPlatforms.indexOf(platform) !== -1 && (window.navigator as any).standalone !== undefined) {
      os = "iPad";
    } else if (macosPlatforms.indexOf(platform) !== -1) {
      os = "macOS";
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = platform;
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = "Windows";
    } else if (/Android/.test(userAgent)) {
      os = "Android";
    }
    return os;
  };
  const defaultDevice = useMemo(() => getDefaultDevice(), []);
  const [device, setDevice] = useState(defaultDevice);

  return (
    <>
      <TextField
        type="text"
        value={device}
        onChange={(e) => {
          setDevice(e.target.value);
        }}
        notices={["32文字以内で入力してください","利用可能な文字は、半角のアルファベット小文字・大文字、数字、半角スペース、-(ハイフン)、_(アンダーバー)と.(ドット)になります"]}
        label="登録する端末の名前を入力してください。"
        id="input-device-name"
        maxLength={32}
      />

      <ButtonWrapper
        textSize={TextSize.SMALL}
        col={1}
        colPc={1}
        buttons={[
          {
            text: "この名前で登録する",
            type: ButtonType.SUBMIT,
            priority: ButtonPriority.HIGH,
            disabled: props.isProcessing,
            onClick: () => {
              const deviceName = device.trim();
              onLoginClick(deviceName);
            },
          },
          {
            text: "戻る",
            type: ButtonType.SUBMIT,
            priority: ButtonPriority.BACK,
            onClick: () => {
              history.goBack();
            },
          },
        ]}
      />
    </>
  );
};

interface BioRegistrationSuccessPresentationProps {
  onConfirmButtonClick: () => void;
}

const BioRegistrationSuccessPresentation = (
  props: BioRegistrationSuccessPresentationProps
) => {
  const { onConfirmButtonClick } = props;
  return (
    <>
      <PageTitle
        subText="生体認証ログイン設定"
        text="登録完了"
        title="生体認証の登録完了"
      />
      <Text
        textItems={[
          {
            text: "登録が完了しました",
          },
        ]}
      />
      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "登録内容を確認する",
            type: ButtonType.BUTTON,
            priority: ButtonPriority.HIGH,
            onClick: () => {
              onConfirmButtonClick();
            },
          },
        ]}
      />
    </>
  );
};

const BioRegistrationFailPresentation = (props: {
  error: string;
  onBackButtonClick: Function;
}) => {
  return (
    <>
      <PageTitle 
        subText="生体認証ログイン設定"
        text="生体認証の登録" 
      />
      <Text
        textItems={[
          {
            text: props.error,
          },
        ]}
      />
      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "戻る",
            type: ButtonType.BUTTON,
            priority: ButtonPriority.BACK,
            onClick: () => {
              props.onBackButtonClick();
            },
          },
        ]}
      />
    </>
  );
};

const ErrorMessageForm = (props: { errorMessages: string }) => {
  const { errorMessages } = props;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem text={errorMessages} textStrong={true} />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const WebAuthApiErrorMessageForm = () => {
  const errorMessage = WEBAUTHN_API_ERROR;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem text={errorMessage} textStrong={true}>
      <a href={BIOMETRIC_INFO_PAGE_URL_WITH_ANCHOR_03}>{WEBAUTHN_API_ERROR_TEXT_LINK}</a>
      {WEBAUTHN_API_ERROR_2}<Link to="/authenticator-manager#anc-01">{WEBAUTHN_API_ERROR_2_TEXT_LINK}</Link>
      {WEBAUTHN_API_ERROR_3}
    </UnOrderedListItem>
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const MaxDeviceErrorMessageForm = () => {
  const maxDeviceErrorMessage = MAX_DEVICE_ERROR;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem
      text={maxDeviceErrorMessage}
      children={
        <>
          <Link to="/authenticator-manager#anc-01">登録済みの端末一覧</Link>
          から不要な端末を削除してください。
        </>
      }
      textStrong={true}
    />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const BrowserNotSupportErrorMessageForm = () => {
  const browserNotSupportErrorMessage = BROWSER_NOT_SUPPORT_ERROR;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem
      text={browserNotSupportErrorMessage}
      children={
        <>
          <img
            src={BlankIcon}
            width="12"
            height="10"
            alt="新しいウィンドウで開く"
            className={`icn-_blank`}
          />
          <a href={BIOMETRIC_INFO_PAGE_URL_WITH_ANCHOR_03} target="_blank">利用可能な環境</a>
          をご確認の上、別のブラウザでお試しください。
        </>
      }
      textStrong={true}
    />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

export { BiometricRegistrationContainer as BiometricRegistration };
